import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  listarGruposEmpresaVinculo,
  listarUsuariosAtivosVinculo,
  listarContasElegiveis,
  listarVinculosContaUsuario,
  incluirLoteVinculoContaUsuario,
  excluirVinculoContaUsuario,
  excluirLoteVinculoContaUsuario,
} from '~/Services/Service';
import GridVinculosAtuais from './components/GridVinculosAtuais';
import ModalVincularContas from './components/ModalVincularContas';
import ModalConfirmarExclusao from './components/ModalConfirmarExclusao';

const extrairMensagemErro = (err) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  (err && err.response && err.response.data) ||
  (err && err.message) ||
  'Erro ao processar. Tente novamente.';

const chaveVinculo = (v) => v.codigo_usuario + '_' + v.codigo_conta;

const ContasUsuario = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [usuariosAtivos, setUsuariosAtivos] = useState([]);

  // Filtros de pesquisa
  const [codigoGrupoEmpresa, setCodigoGrupoEmpresa] = useState(null);
  const [codigoUsuario, setCodigoUsuario] = useState(null);
  const [codigoConta, setCodigoConta] = useState(null);
  const [contasFiltro, setContasFiltro] = useState([]);

  const [vinculosAtuais, setVinculosAtuais] = useState([]);
  const [vinculosSelecionados, setVinculosSelecionados] = useState(new Set());
  const [pesquisado, setPesquisado] = useState(false);

  const [modalVincular, setModalVincular] = useState(false);
  const [modalExclusao, setModalExclusao] = useState({
    aberto: false,
    itens: [],
    nomeUsuario: '',
    onConfirmar: null,
  });

  const [loading, setLoading] = useState(false);

  // Mount: carrega GEs e usuários para os selects
  useEffect(() => {
    const carregar = async () => {
      try {
        const [ges, usuarios] = await Promise.all([
          listarGruposEmpresaVinculo(),
          listarUsuariosAtivosVinculo(),
        ]);
        setGruposEmpresa(ges || []);
        setUsuariosAtivos(usuarios || []);
      } catch (err) {
        toast.error('Erro ao carregar dados iniciais.');
      }
    };
    carregar();
  }, []);

  // Quando GE muda: carrega contas do filtro e limpa dependentes
  useEffect(() => {
    setCodigoUsuario(null);
    setCodigoConta(null);
    setContasFiltro([]);
    setVinculosAtuais([]);
    setVinculosSelecionados(new Set());
    setPesquisado(false);

    if (!codigoGrupoEmpresa) return;

    const carregarContas = async () => {
      try {
        const resp = await listarContasElegiveis(codigoGrupoEmpresa, null);
        setContasFiltro(resp || []);
      } catch (err) {
        // silencioso — filtro de conta é opcional
      }
    };
    carregarContas();
  }, [codigoGrupoEmpresa]);

  const opcoesGE = (gruposEmpresa || []).map((ge) => ({
    value: ge.codigo_grupo_empresa,
    label: ge.codigo_grupo_empresa + ' - ' + ge.nome,
  }));

  const opcoesUsuarios = (usuariosAtivos || []).map((u) => ({
    value: u.codigo_usuario,
    label: u.codigo_usuario + ' - ' + u.nome,
  }));

  // listarContasElegiveis retorna { codigo_conta, nome, tipo_descricao }
  const opcoesContas = (contasFiltro || []).map((c) => ({
    value: c.codigo_conta,
    label: c.codigo_conta + ' - ' + (c.nome || ''),
  }));

  const valorGE = opcoesGE.find((o) => o.value === codigoGrupoEmpresa) || null;
  const valorUsuario = opcoesUsuarios.find((o) => o.value === codigoUsuario) || null;
  const valorConta = opcoesContas.find((o) => o.value === codigoConta) || null;

  const handleChangeGE = (opcao) => {
    setCodigoGrupoEmpresa(opcao ? opcao.value : null);
  };

  const handleChangeUsuario = (opcao) => {
    setCodigoUsuario(opcao ? opcao.value : null);
  };

  const handleChangeConta = (opcao) => {
    setCodigoConta(opcao ? opcao.value : null);
  };

  const enriquecerVinculos = (vinculos, nomeGE) =>
    (vinculos || []).map((v) => {
      const usuarioObj = usuariosAtivos.find((u) => u.codigo_usuario === v.codigo_usuario);
      return {
        ...v,
        nome_grupo_empresa: nomeGE,
        nome_usuario: usuarioObj ? usuarioObj.nome : String(v.codigo_usuario),
      };
    });

  // Pesquisar: GE obrigatória, Usuário e Conta opcionais
  const handlePesquisar = async () => {
    if (!codigoGrupoEmpresa) {
      toast.warn('Selecione um Grupo de Empresa.');
      return;
    }
    setLoading(true);
    try {
      const resp = await listarVinculosContaUsuario(
        codigoGrupoEmpresa,
        codigoUsuario,
        codigoConta
      );
      const nomeGE = valorGE ? valorGE.label : String(codigoGrupoEmpresa);
      setVinculosAtuais(enriquecerVinculos(resp, nomeGE));
      setVinculosSelecionados(new Set());
      setPesquisado(true);
    } catch (err) {
      toast.error('Erro ao pesquisar vínculos.');
    } finally {
      setLoading(false);
    }
  };

  // Recarregar após inclusão/exclusão
  const recarregarVinculos = async () => {
    if (!codigoGrupoEmpresa) return;
    try {
      const resp = await listarVinculosContaUsuario(
        codigoGrupoEmpresa,
        codigoUsuario,
        codigoConta
      );
      const nomeGE = valorGE ? valorGE.label : String(codigoGrupoEmpresa);
      setVinculosAtuais(enriquecerVinculos(resp, nomeGE));
      setVinculosSelecionados(new Set());
    } catch (err) {
      toast.error('Erro ao recarregar vínculos.');
    }
  };

  // Abrir modal de vínculo (independente dos filtros)
  const handleAbrirModalVincular = () => {
    setModalVincular(true);
  };

  const handleFecharModalVincular = () => {
    setModalVincular(false);
  };

  const handleConfirmarVinculo = async (codigosSelecionados, geModal, usuarioModal) => {
    setModalVincular(false);
    setLoading(true);
    try {
      await incluirLoteVinculoContaUsuario({
        codigo_grupo_empresa: geModal,
        codigo_usuario: usuarioModal,
        codigos_conta: codigosSelecionados,
      });
      const qtd = codigosSelecionados.length;
      toast.success(qtd + (qtd === 1 ? ' vínculo criado com sucesso.' : ' vínculos criados com sucesso.'));
      if (pesquisado) await recarregarVinculos();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  // Toggle seleção de vínculo
  const handleToggleVinculo = (vinculo) => {
    const chave = chaveVinculo(vinculo);
    setVinculosSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(chave)) {
        novo.delete(chave);
      } else {
        novo.add(chave);
      }
      return novo;
    });
  };

  const handleToggleAllVinculos = () => {
    if (vinculosSelecionados.size === vinculosAtuais.length) {
      setVinculosSelecionados(new Set());
    } else {
      setVinculosSelecionados(new Set(vinculosAtuais.map(chaveVinculo)));
    }
  };

  // Exclusão em lote
  const handleAbrirExclusaoLote = () => {
    const itensSelecionados = vinculosAtuais.filter((v) =>
      vinculosSelecionados.has(chaveVinculo(v))
    );
    const usuariosDistintos = new Set(itensSelecionados.map((v) => v.codigo_usuario));
    const nomeUsuario = usuariosDistintos.size === 1
      ? (itensSelecionados[0].nome_usuario || String(itensSelecionados[0].codigo_usuario))
      : null;

    setModalExclusao({
      aberto: true,
      itens: itensSelecionados,
      nomeUsuario: nomeUsuario,
      onConfirmar: handleConfirmarExclusaoLote,
    });
  };

  const handleConfirmarExclusaoLote = async (itens) => {
    setModalExclusao({ aberto: false, itens: [], nomeUsuario: '', onConfirmar: null });
    setLoading(true);
    try {
      await excluirLoteVinculoContaUsuario({
        codigo_grupo_empresa: codigoGrupoEmpresa,
        itens: itens.map((v) => ({
          codigo_usuario: v.codigo_usuario,
          codigo_conta: v.codigo_conta,
        })),
      });
      const qtd = itens.length;
      toast.success(qtd + (qtd === 1 ? ' vínculo removido com sucesso.' : ' vínculos removidos com sucesso.'));
      await recarregarVinculos();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  // Exclusão individual
  const handleExcluirIndividual = (vinculo) => {
    const nomeUsuario = vinculo.nome_usuario || String(vinculo.codigo_usuario);
    const itens = [{
      codigo_usuario: vinculo.codigo_usuario,
      codigo_conta: vinculo.codigo_conta,
      nome_conta: vinculo.nome_conta,
    }];

    setModalExclusao({
      aberto: true,
      itens: itens,
      nomeUsuario: nomeUsuario,
      onConfirmar: async () => {
        setModalExclusao({ aberto: false, itens: [], nomeUsuario: '', onConfirmar: null });
        setLoading(true);
        try {
          await excluirVinculoContaUsuario({
            codigo_grupo_empresa: vinculo.codigo_grupo_empresa,
            codigo_usuario: vinculo.codigo_usuario,
            codigo_conta: vinculo.codigo_conta,
          });
          toast.success('Vínculo removido com sucesso.');
          await recarregarVinculos();
        } catch (err) {
          toast.error(extrairMensagemErro(err));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleFecharModalExclusao = () => {
    setModalExclusao({ aberto: false, itens: [], nomeUsuario: '', onConfirmar: null });
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Contas de Usuário" />

      {/* Filtros */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Grupo de Empresa <span className="text-danger">*</span>
              </Form.Label>
              <Select
                placeholder="Selecione..."
                options={opcoesGE}
                value={valorGE}
                onChange={handleChangeGE}
                isClearable
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Usuário
              </Form.Label>
              <Select
                placeholder={codigoGrupoEmpresa ? 'Todos os usuários' : 'Selecione um Grupo de Empresa'}
                options={opcoesUsuarios}
                value={valorUsuario}
                onChange={handleChangeUsuario}
                isClearable
                isDisabled={!codigoGrupoEmpresa}
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Conta
              </Form.Label>
              <Select
                placeholder={codigoGrupoEmpresa ? 'Todas as contas' : 'Selecione um Grupo de Empresa'}
                options={opcoesContas}
                value={valorConta}
                onChange={handleChangeConta}
                isClearable
                isDisabled={!codigoGrupoEmpresa}
                components={{ DropdownIndicator }}
                styles={customStyles}
              />
            </Col>
            <Col md={3} className="mb-2 mb-md-0 d-flex align-items-end" style={{ gap: '8px' }}>
              <Button
                variant="primary"
                onClick={handlePesquisar}
                disabled={loading}
                style={{ height: '38px', whiteSpace: 'nowrap' }}
              >
                <FaSearch className="mr-1" /> Pesquisar
              </Button>
              <Button
                variant="success"
                onClick={handleAbrirModalVincular}
                disabled={loading}
                style={{ height: '38px', whiteSpace: 'nowrap' }}
              >
                <FaPlus className="mr-1" /> Vincular
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Grid de resultados */}
      <Card className="shadow-sm">
        <Card.Header
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
          className="py-2 d-flex justify-content-between align-items-center"
        >
          <strong>{valorGE ? 'Vínculos de ' + valorGE.label : 'Vínculos'}</strong>
          {vinculosSelecionados.size > 0 && (
            <Button
              size="sm"
              variant="danger"
              onClick={handleAbrirExclusaoLote}
              disabled={loading}
            >
              <FaTrash className="mr-1" /> Excluir {vinculosSelecionados.size} selecionado(s)
            </Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <GridVinculosAtuais
            vinculos={vinculosAtuais}
            selecionados={vinculosSelecionados}
            pesquisado={pesquisado}
            onToggle={handleToggleVinculo}
            onToggleAll={handleToggleAllVinculos}
            onExcluirIndividual={handleExcluirIndividual}
          />
        </Card.Body>
      </Card>

      <ModalVincularContas
        aberto={modalVincular}
        gruposEmpresa={gruposEmpresa}
        usuariosAtivos={usuariosAtivos}
        onVincular={handleConfirmarVinculo}
        onFechar={handleFecharModalVincular}
      />

      <ModalConfirmarExclusao
        aberto={modalExclusao.aberto}
        itens={modalExclusao.itens}
        nomeUsuario={modalExclusao.nomeUsuario}
        onConfirmar={() => {
          if (modalExclusao.onConfirmar) {
            modalExclusao.onConfirmar(modalExclusao.itens);
          }
        }}
        onCancelar={handleFecharModalExclusao}
      />
    </Container>
  );
};

export default ContasUsuario;
